import browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { CommandEventType, CommandName, CommandVersion, defaultLocale, maximumProcessableCommands, } from "./command-library.types";
import * as commandStorage from "./command-storage";
import { getStartInfo, setStartInfo } from "./start-info";
import { recordEvent, recordGenericEvent } from "./event-recording";
const knownCommandsList = Object.values(CommandName);
const actorByCommandName = new Map();
const unexecutableCommands = new Map();
const serverLogger = new ewe.telemetry.ServerLogger("in_product_messaging");
let localeOverride = null;
let isActive = false;
function isCommand(candidate) {
    return (typeof candidate === "object" &&
        candidate !== null &&
        "version" in candidate &&
        "ipm_id" in candidate &&
        "command_name" in candidate);
}
export function isCommandMap(candidate) {
    return (typeof candidate === "object" &&
        candidate !== null &&
        Object.keys(candidate).every((key) => typeof key === "string") &&
        Object.values(candidate).every(isCommand));
}
export function setCommandActor(commandName, actor) {
    actorByCommandName.set(commandName, actor);
    retryExecuteCommands(commandName);
}
export function dismissCommand(ipmId) {
    const { getDismissedIpmIds, setDismissedIpmIds } = getStartInfo();
    const dismissedIpmIds = getDismissedIpmIds();
    if (!dismissedIpmIds.includes(ipmId)) {
        dismissedIpmIds.push(ipmId);
        setDismissedIpmIds(dismissedIpmIds);
    }
    commandStorage.remove(ipmId);
}
export function getBehavior(ipmId) {
    const command = getCommand(ipmId);
    if (!command) {
        return null;
    }
    const actor = actorByCommandName.get(command.command_name);
    if (!actor) {
        return null;
    }
    return actor.getBehavior(command);
}
export function getCommand(ipmId) {
    return commandStorage.get(ipmId);
}
export function getContent(ipmId) {
    var _a;
    const command = getCommand(ipmId);
    if (!command) {
        return null;
    }
    const actor = actorByCommandName.get(command.command_name);
    if (!actor) {
        return null;
    }
    const locale = localeOverride !== null && localeOverride !== void 0 ? localeOverride : browser.i18n.getUILanguage();
    const content = getContentForLocale(locale, (_a = command.content) !== null && _a !== void 0 ? _a : {});
    return actor.getContent(command, content);
}
export function isCommandExpired(command) {
    return commandStorage.get(command.ipm_id) === null;
}
async function matchesCohort(command) {
    if (!command.cohort) {
        return true;
    }
    const cohorts = await ewe.cohorts.getAll();
    return cohorts.includes(command.cohort);
}
function getContentForLocale(locale, contentByLocale) {
    if (locale in contentByLocale) {
        return contentByLocale[locale];
    }
    const [language] = locale.split("-", 1);
    if (language in contentByLocale) {
        return contentByLocale[language];
    }
    if (defaultLocale in contentByLocale) {
        return contentByLocale[defaultLocale];
    }
    return {};
}
function retryExecuteCommands(commandName) {
    for (const [command, executionArguments] of unexecutableCommands) {
        if (command.command_name !== commandName) {
            continue;
        }
        unexecutableCommands.delete(command);
        void executeIPMCommands([command], ...executionArguments);
    }
}
export async function executeIPMCommands(commands, isInitialization = false, isConfigUpdate = false) {
    if (!isActive) {
        return;
    }
    const { logger } = getStartInfo();
    const actorByExecutableCommand = new Map();
    if (commands.length > maximumProcessableCommands) {
        logger.reportAndLogError(new Error("[ipm]: Too many commands received."));
        recordGenericEvent("too_many_commands");
        return;
    }
    for (const command of commands) {
        if (!isCommand(command)) {
            logger.reportAndLogError(new Error("[ipm]: Invalid command received."));
            continue;
        }
        if (!knownCommandsList.includes(command.command_name)) {
            logger.reportAndLogError(new Error(`[ipm]: Unknown command name received: ${command.command_name}`));
            continue;
        }
        if (command.version !== CommandVersion[command.command_name]) {
            logger.reportAndLogError(new Error(`[ipm]: Command version mismatch for command "${command.command_name}". Requested version was ${command.version}, version present is ${CommandVersion[command.command_name]}`));
            if (command.version < CommandVersion[command.command_name]) {
                dismissCommand(command.ipm_id);
            }
            continue;
        }
        if (isCommandExpired(command)) {
            logger.reportAndLogError(new Error("[ipm]: Command has expired."));
            recordEvent(command.ipm_id, command.command_name, CommandEventType.expired);
            dismissCommand(command.ipm_id);
            continue;
        }
        const actor = actorByCommandName.get(command.command_name);
        if (!actor) {
            logger.debug("[ipm]: No actor found:", command.command_name);
            unexecutableCommands.set(command, [isInitialization, isConfigUpdate]);
            continue;
        }
        if (!actor.isValidCommand(command)) {
            logger.reportAndLogError(new Error("[ipm]: Invalid parameters received."));
            continue;
        }
        actorByExecutableCommand.set(command, actor);
    }
    const commandHandlingPromises = Array.from(actorByExecutableCommand).map(async ([executableCommand, actor]) => {
        if (isConfigUpdate) {
            serverLogger.behavior("command_received", executableCommand.ipm_id, executableCommand.ipm_id);
        }
        if (!(await matchesCohort(executableCommand))) {
            logger.debug("[ipm]: Cohort doesn't match.");
            return;
        }
        await actor.handleCommand(executableCommand.ipm_id, isInitialization);
    });
    await Promise.all(commandHandlingPromises).then(() => {
        knownCommandsList
            .map((name) => actorByCommandName.get(name))
            .forEach((actor) => actor === null || actor === void 0 ? void 0 : actor.onCommandsProcessed());
    });
}
export async function clearCommands() {
    for (const actor of actorByCommandName.values()) {
        await actor.clearCommands();
    }
}
async function getRemoteCommands() {
    const { logger } = getStartInfo();
    const remoteCommandCandidates = await ewe.remoteConfig.getIpmCommands();
    if (remoteCommandCandidates === null) {
        return null;
    }
    const remoteCommands = [];
    for (const candidate of remoteCommandCandidates) {
        if (!isCommand(candidate)) {
            logger.reportAndLogError(new Error("[ipm]: Invalid command received."));
            continue;
        }
        remoteCommands.push(candidate);
    }
    return remoteCommands;
}
function clearDismissedIpmIds() {
    const { setDismissedIpmIds } = getStartInfo();
    setDismissedIpmIds([]);
}
function handleTestingGetCommandsMessage() {
    return commandStorage.getAll();
}
function handleTestingOverrideLocale(message) {
    if (!isTestingOverrideLocaleMessage(message)) {
        return;
    }
    localeOverride = message.locale;
}
function isTestingOverrideLocaleMessage(candidate) {
    return (candidate !== null && typeof candidate === "object" && "locale" in candidate);
}
async function synchronizeRemoteCommands() {
    var _a;
    const { getDismissedIpmIds, getLastSyncId, info, logger, setDismissedIpmIds, setLastSyncId, } = getStartInfo();
    const lastSyncId = getLastSyncId();
    const currentSyncId = (_a = (await ewe.remoteConfig.getRevisionId())) !== null && _a !== void 0 ? _a : "";
    const isConfigUpdate = currentSyncId !== lastSyncId;
    if (isConfigUpdate) {
        setLastSyncId(currentSyncId);
    }
    const remoteCommands = await getRemoteCommands();
    if (remoteCommands === null) {
        logger.reportAndLogError(new Error("[ipm]: No remote configuration."));
        return;
    }
    if (info.getBrowserName() === "firefox") {
        return;
    }
    const dismissedIpmIds = getDismissedIpmIds();
    const commands = remoteCommands.filter((command) => {
        return !dismissedIpmIds.includes(command.ipm_id);
    });
    const newDismissedIpmIds = dismissedIpmIds.filter((ipmId) => {
        return remoteCommands.some((command) => command.ipm_id === ipmId);
    });
    setDismissedIpmIds(newDismissedIpmIds);
    const commandMap = Object.fromEntries(commands.map((command) => [command.ipm_id, command]));
    commandStorage.replaceAll(commandMap);
    await executeIPMCommands(commands, true, isConfigUpdate);
}
export async function start(startInfo) {
    setStartInfo(startInfo);
    isActive = true;
    void synchronizeRemoteCommands();
    ewe.remoteConfig.onChanged.addListener(synchronizeRemoteCommands);
    startInfo.addMessageListener("ipm:testing.clearDismissedIpmIds", clearDismissedIpmIds);
    startInfo.addMessageListener("ipm:testing.getCommands", handleTestingGetCommandsMessage);
    startInfo.addMessageListener("ipm:testing.overrideLocale", handleTestingOverrideLocale);
}
export async function stop() {
    const { removeMessageListener } = getStartInfo();
    isActive = false;
    await clearCommands();
    ewe.remoteConfig.onChanged.removeListener(synchronizeRemoteCommands);
    removeMessageListener("ipm:testing.clearDismissedIpmIds", clearDismissedIpmIds);
    removeMessageListener("ipm:testing.getCommands", handleTestingGetCommandsMessage);
    removeMessageListener("ipm:testing.overrideLocale", handleTestingOverrideLocale);
}
